import React, { useState, useEffect } from 'react';
import { useAuth } from '../../components/Auth/AuthContext';
import { useApp } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Heart, MapPin, Eye, MoreHorizontal, ChevronLeft, ChevronRight, Expand } from 'lucide-react';

export default function LikedSpotsSection() {
  const { token } = useAuth();
  const { actions } = useApp();
  const [likedSpots, setLikedSpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [imageIndices, setImageIndices] = useState({}); // Track current image index for each spot

  useEffect(() => {
    fetchLikedSpots();
  }, []);

  const fetchLikedSpots = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/liked-spots`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const spots = await response.json();
        setLikedSpots(spots);
      } else {
        console.error('Failed to fetch liked spots');
      }
    } catch (error) {
      console.error('Error fetching liked spots:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewSpotDetails = (spot) => {
    actions.setSelectedSpot(spot);
    actions.setShowSpotDetails(true);
  };

  const navigateImage = (spotId, direction, e) => {
    e.stopPropagation(); // Prevent card click
    
    const spot = likedSpots.find(s => s.id === spotId);
    const total = spot?.photos?.length || 0;
    if (total <= 1) return;
    
    setImageIndices(prev => {
      const currentIndex = prev[spotId] || 0;
      const newIndex = direction === 'next' 
        ? (currentIndex + 1) % total
        : (currentIndex - 1 + total) % total;
      
      return {
        ...prev,
        [spotId]: newIndex
      };
    });
  };

  const getCurrentImageIndex = (spotId) => {
    return imageIndices[spotId] || 0;
  };

  const displaySpots = showAll ? likedSpots : likedSpots.slice(0, 6);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Chargement de vos spots likés...</div>
        </CardContent>
      </Card>
    );
  }

  if (likedSpots.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center py-12">
            <Heart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600">Aucun spot liké</h3>
            <p className="text-gray-500">
              Explorez et likez des spots pour les retrouver ici !
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Mes spots likés ({likedSpots.length})</h2>
        {likedSpots.length > 6 && (
          <Button
            variant="outline"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? 'Voir moins' : 'Voir tout'}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {displaySpots.map((spot) => {
          const currentImageIndex = getCurrentImageIndex(spot.id);
          
          return (
            <Card key={spot.id} className="overflow-hidden">
              {/* Spot Image with Carousel */}
              {spot.photos && spot.photos.length > 0 && (
                <div className="aspect-video relative group">
                  <img
                    src={spot.photos[currentImageIndex].url || spot.photos[currentImageIndex]}
                    alt={spot.photos[currentImageIndex].metadata?.title || spot.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 cursor-pointer"
                    onClick={() => handleViewSpotDetails(spot)}
                  />
                  
                  {/* Expand overlay on hover */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center pointer-events-none">
                    <Expand className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" size={32} />
                  </div>
                  
                  {/* Navigation arrows - only if multiple photos */}
                  {spot.photos.length > 1 && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => navigateImage(spot.id, 'prev', e)}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white hover:bg-opacity-70 opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-8 h-8 p-0"
                      >
                        <ChevronLeft size={18} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => navigateImage(spot.id, 'next', e)}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white hover:bg-opacity-70 opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-8 h-8 p-0"
                      >
                        <ChevronRight size={18} />
                      </Button>
                    </>
                  )}
                  
                  {/* Photo count indicator - only if multiple photos */}
                  {spot.photos.length > 1 && (
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded-full text-xs">
                      {currentImageIndex + 1}/{spot.photos.length}
                    </div>
                  )}
                  
                  {/* Like badge */}
                  <div className="absolute top-2 right-2">
                    <Badge variant="secondary" className="bg-red-500 text-white">
                      <Heart className="h-3 w-3 mr-1 fill-current" />
                      {spot.likes}
                    </Badge>
                  </div>
                </div>
              )}

              <CardContent className="p-4">
                <h3 className="font-semibold text-lg mb-2">{spot.title}</h3>
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                  {spot.description}
                </p>

                {/* Tags */}
                {spot.tags && spot.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {spot.tags.slice(0, 3).map((tag, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                    {spot.tags.length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{spot.tags.length - 3}
                      </Badge>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-500">
                    <MapPin className="h-4 w-4 mr-1" />
                    Détails
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleViewSpotDetails(spot)}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Voir
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}